import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import dayjs from "dayjs";
import { getGames } from "../../api/games.api";
import { tournamentParticipantMode } from "../../util/tournamentParticipantMode.enum";
import { tournamentSystem } from "../../util/tournamentSystem.enum";

const CreateTournamentModal = ({
  open,
  selectedEvent,
  handleClose,
  handleSubmit,
  tournamentData,
  admins,
}) => {
  const [games, setGames] = useState([]);

  const [formData, setFormData] = useState({
    id: null,
    eventId: null,
    name: "",
    game: "",
    participantsPerMatch: 2,
    winnerPerMatch: 1,
    description: "",
    start: dayjs(),
    end: dayjs(),
    system: tournamentSystem[0],
    participantMode: tournamentParticipantMode[0],
    teamSize: 0,
    participantCountMax: 0,
    participantCountMin: 0,
    prize: "",
    isMainTournament: false,
    admin: "",
  });

  const initialFormData = useMemo(
    () => ({
      id: null,
      eventId: null,
      name: "",
      game: "",
      participantsPerMatch: 2,
      winnerPerMatch: 1,
      description: "",
      start: dayjs(),
      end: dayjs(),
      system: tournamentSystem[0],
      participantMode: tournamentParticipantMode[0],
      teamSize: 0,
      participantCountMax: 0,
      participantCountMin: 0,
      prize: "",
      isMainTournament: false,
      admin: "",
    }),
    [],
  );

  useEffect(() => {
    getGames().then((response) => {
      setGames(response.data);
      if (tournamentData && tournamentData.game) {
        setFormData({
          id: tournamentData.id || "",
          eventId: tournamentData.eventId || "",
          name: tournamentData.name || "",
          game: response.data.find(
            (game) => game.id === tournamentData.game.id,
          ),
          participantsPerMatch: tournamentData.participantsPerMatch || 2,
          winnerPerMatch: tournamentData.winnerPerMatch || 1,
          description: tournamentData.description || "",
          start: dayjs(tournamentData.start) || dayjs(),
          end: dayjs(tournamentData.end) || dayjs(),
          system: tournamentData.system || "",
          participantMode: tournamentData.participantMode || "",
          teamSize: tournamentData.teamSize || 0,
          participantCountMax: tournamentData.participantCountMax || 0,
          participantCountMin: tournamentData.participantCountMin || 0,
          prize: tournamentData.prize || "",
          isMainTournament: tournamentData.isMainTournament || false,
          admin: tournamentData.admin || "",
        });
      } else {
        setFormData(initialFormData);
      }
    });
  }, [selectedEvent, tournamentData, initialFormData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevState) => {
      const updatedFormData = {
        ...prevState,
        [name]: type === "checkbox" ? checked : value,
      };

      // Überprüfen, ob `system` nicht "KO" ist und `participantsPerMatch` gesetzt werden muss
      if (name === "system" && value !== "KO") {
        updatedFormData.participantsPerMatch = 2;
      }
      if (name === "system" && value === "GroupKO") {
        updatedFormData.winnerPerMatch = 1;
      }

      if (name === "participantMode" && value === "Single") {
        updatedFormData.teamSize = 0;
      }

      return updatedFormData;
    });
  };

  const handleStartDateChange = (newValue) => {
    setFormData((prevState) => ({
      ...prevState,
      start: newValue,
    }));
  };

  const handleEndDateChange = (newValue) => {
    setFormData((prevState) => ({
      ...prevState,
      end: newValue,
    }));
  };

  const onSubmit = (e) => {
    e.preventDefault();
    handleSubmit(formData);
    setFormData(initialFormData);
    handleClose();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog open={open} onClose={handleClose}>
        <DialogContent>
          <form onSubmit={onSubmit}>
            <Typography variant="h3">
              {tournamentData ? "Edit Tournament" : "Create Tournament"}
            </Typography>
            <div>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.isMainTournament}
                    name="isMainTournament"
                    onChange={(e) => handleChange(e)}
                  />
                }
                label="Main Tournament"
              />
            </div>
            <div>
              <TextField
                id="name"
                label="Name"
                name="name"
                variant="outlined"
                fullWidth
                value={formData.name}
                onChange={(e) => handleChange(e)}
              />
            </div>
            <div>
              <TextField
                id="description"
                label="Description"
                name="description"
                variant="outlined"
                fullWidth
                multiline
                rows={4}
                value={formData.description}
                onChange={(e) => handleChange(e)}
              />
            </div>
            <div>
              <TextField
                id="prize"
                label="Prize"
                name="prize"
                variant="outlined"
                fullWidth
                value={formData.prize}
                onChange={(e) => handleChange(e)}
              />
            </div>
            <div>
              <FormControl fullWidth>
                <InputLabel id="select-game-label">Game</InputLabel>
                <Select
                  labelId="select-game-label"
                  id="game"
                  name="game"
                  value={formData.game}
                  label="Game"
                  onChange={handleChange}
                >
                  {games.map((game) => (
                    <MenuItem key={game.id} value={game}>
                      {game.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div>
              <TextField
                disabled={formData.system === "GroupKO"}
                id="winnerPerMatch"
                label="Winner per Match"
                name="winnerPerMatch"
                variant="outlined"
                type="name"
                fullWidth
                value={formData.winnerPerMatch}
                onChange={(e) => handleChange(e)}
              />
            </div>
            <div>
              <TextField
                disabled={formData.system !== "KO"}
                id="participantsPerMatch"
                label="Participants per Match"
                name="participantsPerMatch"
                variant="outlined"
                type="number"
                fullWidth
                value={formData.participantsPerMatch}
                onChange={(e) => handleChange(e)}
              />
            </div>
            <div>
              <FormControl fullWidth>
                <InputLabel id="select-system-label">System</InputLabel>
                <Select
                  labelId="select-system-label"
                  id="system"
                  name="system"
                  value={formData.system}
                  label="system"
                  onChange={handleChange}
                >
                  {tournamentSystem.map((system) => (
                    <MenuItem key={system} value={system}>
                      {system}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div>
              <FormControl fullWidth>
                <InputLabel id="select-participantMode-label">
                  Participant Mode
                </InputLabel>
                <Select
                  labelId="select-participantMode-label"
                  id="participantMode"
                  name="participantMode"
                  value={formData.participantMode}
                  label="participantMode"
                  onChange={handleChange}
                >
                  {tournamentParticipantMode.map((mode) => (
                    <MenuItem key={mode} value={mode}>
                      {mode}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div>
              <TextField
                disabled={formData.participantMode === "Single"}
                id="teamSize"
                label="Team Size"
                name="teamSize"
                variant="outlined"
                fullWidth
                value={formData.teamSize}
                onChange={(e) => handleChange(e)}
              />
            </div>
            <div>
              <TextField
                id="participantCountMin"
                label="Min. Participants"
                name="participantCountMin"
                variant="outlined"
                fullWidth
                value={formData.participantCountMin}
                onChange={(e) => handleChange(e)}
              />
            </div>
            <div>
              <TextField
                id="participantCountMax"
                label="Max. Participants"
                name="participantCountMax"
                variant="outlined"
                fullWidth
                value={formData.participantCountMax}
                onChange={(e) => handleChange(e)}
              />
            </div>
            <div>
              <DateTimePicker
                label="Start Date & Time"
                value={formData.start}
                onChange={handleStartDateChange}
                inputFormat="DD.MM.YYYY HH:mm:ss"
                slotProps={{
                  textField: { variant: "outlined", fullWidth: true },
                }}
              />
            </div>
            <div>
              <DateTimePicker
                label="End Date & Time"
                value={formData.end}
                onChange={handleEndDateChange}
                inputFormat="DD.MM.YYYY HH:mm:ss"
                slotProps={{
                  textField: { variant: "outlined", fullWidth: true },
                }}
              />
            </div>
            <div>
              <FormControl fullWidth>
                <InputLabel id="select-host-label">Host</InputLabel>
                <Select
                  labelId="select-host-label"
                  id="host"
                  name="admin"
                  value={formData.admin}
                  label="Host"
                  onChange={handleChange}
                >
                  {admins.map((admin) => (
                    <MenuItem key={admin.id} value={admin.id}>
                      {admin.nickname}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button type="submit" onClick={onSubmit}>
            {tournamentData ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default CreateTournamentModal;
